import { useEffect, useState } from 'react';
import { Building2, Loader2, Star, X } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';

import { primaryFirst } from '../../../lib/crm/contactSheet';
import { useCRMStore } from '../../../store/crmStore';
import type { CRMContact } from '../../../types/crm';
import { AccountPickerButton } from '../../bureau/AccountPickerButton';
import { BTN_GHOST, BTN_PRIMARY } from '../../bureau/buttons';
import { FIELD } from '../../bureau/encre';
import { ContactMultiFieldEditor, EMAIL_LABELS, PHONE_LABELS } from '../ContactMultiFieldEditor';
import { SheetBlock } from './EncreSheet';
import { SheetFormFooter } from './SheetFormFooter';
import { createdOrFailure } from '../../../lib/crm/storeGesture';

const LABEL = 'mb-1.5 block text-xs font-medium text-desk-soft';

/**
 * « Modifier » a contact, in its sheet (PLAN-ENCRE-OUTILS O2): the only place the app edits one —
 * the Contacts page's « Modifier » opens this sheet on it. Who it is (names, function, status, the
 * feature notifications), its mails and phones, the accounts it works with (primary, removed, a new
 * one picked by its key). « Terminé » saves what changed: typed words are never dropped by it.
 */
export function ContactSheetEdit({
  contact,
  onChanged,
  onDone,
  onDirty,
}: {
  contact: CRMContact;
  onChanged: () => Promise<void>;
  onDone: () => void;
  /** Whether a typed change is not saved (the sheet then asks before closing). */
  onDirty: (dirty: boolean) => void;
}) {
  const store = useCRMStore(
    useShallow((s) => ({
      updateContact: s.updateContact,
      addContactEmail: s.addContactEmail,
      updateContactEmail: s.updateContactEmail,
      removeContactEmail: s.removeContactEmail,
      setPrimaryEmail: s.setPrimaryEmail,
      addContactPhone: s.addContactPhone,
      updateContactPhone: s.updateContactPhone,
      removeContactPhone: s.removeContactPhone,
      setPrimaryPhone: s.setPrimaryPhone,
      addContactAccount: s.addContactAccount,
      removeContactAccount: s.removeContactAccount,
      setPrimaryAccount: s.setPrimaryAccount,
    })),
  );
  const initial = {
    first_name: contact.first_name ?? '',
    last_name: contact.last_name ?? '',
    title: contact.title ?? '',
    status: contact.status ?? 'active',
    feature_notifications_enabled: contact.feature_notifications_enabled !== false,
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = (Object.keys(initial) as (keyof typeof initial)[]).some((k) => form[k] !== initial[k]);
  useEffect(() => onDirty(changed), [changed, onDirty]);
  useEffect(() => () => onDirty(false), [onDirty]);

  /** A mail, phone or account gesture: saved at once, the contact read again, a failure said. */
  const gesture = async (fn: () => Promise<string | null>) => {
    const failed = await fn();
    setError(failed);
    await onChanged();
  };

  const save = async (then: () => void) => {
    if (!changed) return then();
    const first_name = form.first_name.trim();
    const last_name = form.last_name.trim();
    if (!first_name && !last_name) return setError('Il faut au moins un prénom ou un nom.');
    setSaving(true);
    const failed = await store.updateContact(contact.id, { ...form, first_name, last_name, title: form.title.trim() || null });
    setSaving(false);
    setError(failed);
    if (failed) return;
    await onChanged();
    then();
  };

  const links = primaryFirst(contact.contact_accounts);
  return (
    <div>
      <SheetBlock title="Identité">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className={LABEL}>Prénom</span>
            <input className={FIELD} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </label>
          <label className="block">
            <span className={LABEL}>Nom</span>
            <input className={FIELD} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </label>
          <label className="block">
            <span className={LABEL}>Fonction</span>
            <input className={FIELD} value={form.title} placeholder="Ex. Directrice" onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </label>
          <label className="block">
            <span className={LABEL}>Statut</span>
            <select className={FIELD} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value === 'inactive' ? 'inactive' : 'active' })}>
              <option value="active">Actif</option>
              <option value="inactive">Inactif</option>
            </select>
          </label>
        </div>
        <label className="mt-3 flex items-center gap-2.5 text-sm text-desk-strong">
          <input
            type="checkbox"
            className="h-4 w-4 accent-desk-accent"
            checked={form.feature_notifications_enabled}
            onChange={(e) => setForm({ ...form, feature_notifications_enabled: e.target.checked })}
          />
          Notifications de fonctionnalités
        </label>
      </SheetBlock>

      <SheetBlock title="Mails">
        <ContactMultiFieldEditor
          items={(contact.emails ?? []).map((e) => ({ id: e.id, value: e.email, label: e.label, is_primary: e.is_primary }))}
          fieldType="email"
          labelOptions={EMAIL_LABELS}
          placeholder="adresse@exemple.fr"
          onAdd={(value, label, isPrimary) => gesture(() => createdOrFailure(store.addContactEmail(contact.id, value, label, isPrimary), "L'email n'a pas été ajouté."))}
          onRemove={(id) => gesture(() => store.removeContactEmail(id))}
          onSetPrimary={(id) => gesture(() => store.setPrimaryEmail(contact.id, id))}
          onUpdate={(id, value, label) => gesture(() => store.updateContactEmail(id, { email: value, label }))}
        />
      </SheetBlock>

      <SheetBlock title="Téléphones">
        <ContactMultiFieldEditor
          items={(contact.phones ?? []).map((p) => ({ id: p.id, value: p.phone, label: p.label, is_primary: p.is_primary }))}
          fieldType="phone"
          labelOptions={PHONE_LABELS}
          placeholder="06 12 34 56 78"
          onAdd={(value, label, isPrimary) => gesture(() => createdOrFailure(store.addContactPhone(contact.id, value, label, isPrimary), "Le téléphone n'a pas été ajouté."))}
          onRemove={(id) => gesture(() => store.removeContactPhone(id))}
          onSetPrimary={(id) => gesture(() => store.setPrimaryPhone(contact.id, id))}
          onUpdate={(id, value, label) => gesture(() => store.updateContactPhone(id, { phone: value, label }))}
        />
      </SheetBlock>

      <SheetBlock title="Entreprises" count={links.length}>
        <ul className="m-0 mb-3 list-none p-0">
          {links.map((link) => {
            const name = link.account?.name ?? 'ce compte';
            return (
              <li key={link.id} className="flex min-w-0 items-center gap-2 py-1">
                <button
                  type="button"
                  onClick={() => !link.is_primary && void gesture(() => store.setPrimaryAccount(contact.id, link.id))}
                  disabled={link.is_primary}
                  className={`rounded p-1 ${link.is_primary ? 'text-desk-signal' : 'text-desk-faint hover:text-desk-signal'}`}
                  aria-label={link.is_primary ? `${name}, compte principal` : `Faire de ${name} le compte principal`}
                >
                  <Star size={15} fill={link.is_primary ? 'currentColor' : 'none'} aria-hidden="true" />
                </button>
                <Building2 size={15} className="shrink-0 text-desk-soft" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-sm text-desk-ink">{link.account?.name ?? 'Compte'}</span>
                {link.role && <span className="text-xs text-desk-muted">{link.role}</span>}
                <button
                  type="button"
                  onClick={() => void gesture(() => store.removeContactAccount(link.id))}
                  className="rounded p-1 text-desk-faint hover:text-red-600"
                  aria-label={`Retirer ${name}`}
                >
                  <X size={15} aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
        <AccountPickerButton
          value={null}
          label="Rattacher un compte"
          placement="above"
          exclude={links.map((l) => l.account_id)}
          onChange={(account) => {
            if (account) void gesture(() => createdOrFailure(store.addContactAccount(contact.id, account.id, links.length === 0), "Le compte n'a pas été rattaché."));
          }}
        />
      </SheetBlock>

      <SheetFormFooter error={error}>
        {changed && (
          <button type="button" onClick={onDone} disabled={saving} className={BTN_GHOST}>
            Annuler
          </button>
        )}
        <button type="button" onClick={() => void save(onDone)} disabled={saving} className={BTN_PRIMARY}>
          {saving && <Loader2 size={15} className="animate-spin" aria-hidden="true" />}
          {changed ? 'Enregistrer' : 'Terminé'}
        </button>
      </SheetFormFooter>
    </div>
  );
}
